import React, { useMemo } from 'react'
import { createNgReactBridgeDirective } from '../../react-bridge'
import {
  PanelHeader,
  PanelSubtitle
} from '../../../legacy-design-system/configuration-panel/configuration-panel'
import { css, cx } from '@emotion/css'
import { useIntl } from 'react-intl'
import { Text } from '@contrast-security-inc/design-system-typography'
import { theme } from '@contrast-security-inc/design-system-foundations'

export function GenericPanelHeader({
  title,
  subtitle,
  subtitleLink
}: {
  title: string
  subtitle?: string
  subtitleLink?: string
}) {
  return (
    <PanelHeader
      titleI18n={title}
      subtitle={
        subtitle &&
        subtitleLink && (
          <PanelSubtitle subtitleI18n={subtitle} linkI18n={subtitleLink} />
        )
      }
    />
  )
}

export function PanelHeaderWithStatus({
  title,
  statusMap
}: {
  title: string
  statusMap: Array<{
    color: string
    label: string
  }>
}) {
  const { formatMessage } = useIntl()

  return (
    <PanelHeader
      titleI18n={title}
      cornerContent={
        <div
          className={cx(
            'policy-management-panel-header-status-wrapper',
            css`
              margin-top: 4px;
              display: flex;
              flex-direction: row;
              align-items: center;
            `
          )}
        >
          {statusMap.map(({ color, label }, index) => (
            <div
              key={index}
              className={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                :not(:last-child) {
                  margin-right: 16px;
                }
              `}
            >
              <div
                className={css`
                  width: 10px;
                  height: 10px;
                  background-color: ${color};
                `}
              />
              <Text
                font="metadata"
                className={css`
                  text-transform: uppercase;
                  margin-left: 4px;
                `}
              >
                {formatMessage({ id: label })}
              </Text>
            </div>
          ))}
        </div>
      }
    />
  )
}

export function AssessPanelHeader() {
  return (
    <PanelHeaderWithStatus
      title={'title_assessRules'}
      statusMap={[
        {
          label: 'title_ruleModeOff',
          color: theme.color.neutral40
        },
        {
          label: 'title_ruleModeOn',
          color: theme.color.green50
        }
      ]}
    />
  )
}

export function ProtectPanelHeader() {
  const isNorthstarUI = useMemo(() => {
    return window.isEmbeddedInNS === true
  }, [])

  return (
    <PanelHeaderWithStatus
      title={isNorthstarUI ? 'title_adrRules' : 'title_protectRules'}
      statusMap={[
        {
          label: 'title_ruleModeOff',
          color: theme.color.neutral40
        },
        {
          label: 'title_ruleModeMonitorAndMonitorAtPerimeter',
          color: theme.color.black
        },
        {
          label: 'title_ruleModeBlock',
          color: theme.color.green50
        },
        {
          label: 'title_ruleModeBlockAtPerimeter',
          color: theme.color.green30
        }
      ]}
    />
  )
}

export function ShieldsPanelHeader() {
  return (
    <PanelHeaderWithStatus
      title={'title_cveShields'}
      statusMap={[
        {
          label: 'title_ruleModeOff',
          color: theme.color.neutral40
        },
        {
          label: 'title_ruleModeMonitorAndMonitorAtPerimeter',
          color: theme.color.black
        },
        {
          label: 'title_ruleModeBlock',
          color: theme.color.green50
        }
      ]}
    />
  )
}

export const reactAssessPanelHeader = createNgReactBridgeDirective(
  'reactAssessPanelHeader',
  [],
  AssessPanelHeader,
  []
)

export const reactProtectPanelHeader = createNgReactBridgeDirective(
  'reactProtectPanelHeader',
  [],
  ProtectPanelHeader,
  []
)

export const reactShieldsPanelHeader = createNgReactBridgeDirective(
  'reactShieldsPanelHeader',
  [],
  ShieldsPanelHeader,
  []
)

export const reactPanelHeader = createNgReactBridgeDirective(
  'reactPanelHeader',
  [],
  GenericPanelHeader,
  ['title', 'subtitle', 'subtitleLink']
)
